import React, { useState, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  useAsyncSelect,
  FormElement,
  Grid,
  GridCell,
  Flex,
  FlexItem,
  Link,
  LINK_VARIANT_EXTRALIGHT
} from 'mailup-web-components'

import { INPUT_MAX_ITEMS } from 'Constants/index'

import {
  dirtyState,
  inputState,
  INPUT_VALUE_LANDING_PAGE,
  isInputInvalidState
} from 'Pages/UrlSelection/recoil/state/input'
import {
  getLandingPages,
  ORDERBY_MODIFY_DATE,
  ORDERBY_NAME
} from 'Pages/UrlSelection/recoil/actions/actions'
import styled from '@emotion/styled'

const InputFromLanding = () => {
  const intl = useIntl()

  const [options, setOptions] = useState()
  const isInvalid = useRecoilValue(isInputInvalidState)
  const setDirty = useSetRecoilState(dirtyState)
  const [{ radioValue, orderBy }, setInput] = useRecoilState(inputState)
  const [selectDisable, setSelectDisable] = useState(false)

  // async select
  const hints = {
    loading: intl.formatMessage({ id: 'Load.MailupCommon.InputStatusLoading' }),
    moreResults: intl.formatMessage({
      id: 'Load.MailupCommon.InputStatusTooManyItems'
    }),
    noResults: intl.formatMessage({ id: 'Load.Common.NoResultChosen' })
  }
  const getInitialItems = () => getLandingPages({ searchTerm: '', orderBy })
  const getItemsByTerm = searchTerm => getLandingPages({ searchTerm, orderBy })
  const onError = () => {}

  const { onSearch, onChange, ...selectProps } = useAsyncSelect({
    initialValue: undefined,
    getInitialItems,
    getItemsByTerm,
    getItemById: getItemsByTerm,
    onError,
    hints,
    maxItems: INPUT_MAX_ITEMS
  })

  const handleChange = (v = 0) => {
    setInput(prev => ({ ...prev, isDirty: true, lp: v.target.value }))
    setDirty(prev => ({ ...prev, lpDirty: true }))
    onChange(v)
  }

  // ---

  useMemo(() => {
    if (selectProps.options.length > 0) {
      setOptions(selectProps.options.filter(opt => !opt.items))
      setSelectDisable(false)
    } else {
      setSelectDisable(true)
      setOptions([
        {
          value: undefined,
          label: intl.formatMessage({
            id: 'Load.LinkUrlSelection.NoLandingPage'
          })
        }
      ])
    }
  }, [selectProps.options])

  const handleChangeOrder = async () => {
    const newOrderBy =
      orderBy === ORDERBY_NAME ? ORDERBY_MODIFY_DATE : ORDERBY_NAME
    setInput(prev => ({
      ...prev,
      orderBy: newOrderBy
    }))
    const res = await getLandingPages({ orderBy: newOrderBy })

    setOptions(res.items)
  }

  return (
    <>
      <Grid cols={12}>
        <GridCell span={9}>
          <FormElement
            required={radioValue === INPUT_VALUE_LANDING_PAGE}
            label={intl.formatMessage({
              id: 'Load.LinkUrlSelection.SelectLandingPageUrlLabel'
            })}
          >
            <StyledSelect
              placeholder={intl.formatMessage({
                id: 'Load.PostEventConfiguration.SelectLandingPage'
              })}
              disabled={selectDisable}
              className={selectDisable ? 'disabled' : ''}
              onChange={handleChange}
              data-mu-test='select__select-lp'
            >
              {options &&
                options.length > 0 &&
                options.map((item, index) => (
                  <option key={index} value={item.value}>
                    {item.label}
                  </option>
                ))}
            </StyledSelect>
          </FormElement>
        </GridCell>
        <GridCell span={3}>
          {!selectDisable && (
            <Flex alignItems='center'>
              <FlexItem margin='32px 0 0'>
                <Link
                  variant={LINK_VARIANT_EXTRALIGHT}
                  onClick={handleChangeOrder}
                  value={orderBy}
                  data-mu-test='link__change-order-by'
                >
                  {orderBy === ORDERBY_NAME
                    ? intl.formatMessage({
                        id: 'Load.QuickSend.OrderGroupById'
                      })
                    : intl.formatMessage({
                        id: 'Load.QuickSend.OrderGroupByName'
                      })}
                </Link>
              </FlexItem>
            </Flex>
          )}
        </GridCell>
      </Grid>
    </>
  )
}

export default InputFromLanding

const StyledSelect = styled.select`
  position: relative;
  background: #ffffff;
  border: 1px solid #d5dadf;
  border-radius: 4px;
  min-height: 36px;
  font-size: 15px;
  line-height: calc(36px - 2px);
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;
  padding: 0 0 0 8px;
  cursor: pointer;
  &:focus-visible {
    border: 1px solid #d5dadf;
    border-radius: 3px;
    outline: none;
  }
  &:after {
    border: 1px;
  }

  &.disabled {
    background: #e9e9e9;
  }
`
